/**
 * Geode Voice (UPT-2000 lot V0 §5.3) — simulateur vocal (profil local
 * seulement), MÊME interface que la session réelle : une zone de saisie
 * remplace le micro ; chaque phrase validée est traitée comme une
 * transcription finale. Le simulateur appelle `ask_copilot` avec la phrase,
 * affiche la réponse comme transcription de l'assistant et, si la phrase
 * contient « montre » / « affiche », appelle `show_card` avec la réponse.
 * Aucune synthèse vocale (pas de service tiers).
 *
 * UPT-2124 J2a (spec `web/SPEC-UPT-2124-J2.md` §2 n° 5) : « que dois-je faire », « qu'est-ce que je dois faire », « prochaine étape »,
 * « what should i do », « next step » (normalisation sans accents, minuscules) appellent l'outil `next_step` par le MÊME chemin que la session
 * réelle (`executeVoiceTool` → registre) et affichent le texte rendu comme transcription de l'assistant ; hors fiche (outil non enregistré),
 * la réponse est « Ouvre d'abord un dossier du Studio. » (`translate`, fourni par le bouton micro).
 * UPT-2124 J2d (spec `web/SPEC-UPT-2124-J2d.md` §2 n° 1-2) : « crée un dossier {nom} », « nouveau dossier {nom} », « create a dossier {name} » appellent
 * l'outil `create_dossier {name}` (le nom tel que dit, casse et accents gardés) ; quand une confirmation attend, « oui » / « yes » / « ok » → `confirm
 * {decision: 'yes'}`, « non » / « no » / « annule » → `confirm {decision: 'no'}` — toujours par `executeVoiceTool` (registre), comme la session réelle.
 * UPT-2124 J4c (spec `web/SPEC-UPT-2124-J4.md` §3) : la grammaire des gestes d'écran (`ui/uiGrammar.ts`) — « ouvre les prêts », « clique sur … », « mets 12 000
 * dans le montant », « choisis … dans … », « enregistre », « descends », « reviens », « annule » (le dernier remplissage), « qu'est-ce qu'il y a à l'écran » —
 * appelle les outils `ui_*` par le même chemin ; « ouvre X » essaie le menu, puis l'élément de l'écran.
 */
import type { VoiceSession } from './RealtimeVoiceSession';
import { CONFIRM_TOOL_NAME, hasPendingVoiceConfirmation, type VoiceConfirmDecision } from './voiceConfirm';
import { executeVoiceTool } from './voiceTools';
import { useVoiceStore } from './voiceStore';
import { hasVoiceTool } from './voiceToolRegistry';
import { isUndoPhrase, uiToolOf } from './ui/uiGrammar';

export type MockVoiceSessionOptions = {
  pageContext: string;
  /** Traduction d'une clé i18n (la réponse « hors fiche » de `next_step`) ; sans elle, la clé est affichée. */
  translate?: (id: string) => string;
};

/** Nom de l'outil « que dois-je faire ? » (enregistré par la fiche d'un dossier). */
export const NEXT_STEP_TOOL_NAME = 'next_step';

/** Formulations reconnues, déjà normalisées (`normalizePhrase`). */
export const NEXT_STEP_PHRASES: readonly string[] = ['que dois je faire', "qu'est ce que je dois faire", 'prochaine etape', 'what should i do', 'next step'];

/** Minuscules, sans accents, apostrophes droites, traits d'union et espaces réduits à une espace. */
export const normalizePhrase = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\u2019\u2018]/g, "'")
    .replace(/[-\s]+/g, ' ')
    .trim();

/**
 * Interpellation en tête de phrase (« Géode, crée un dossier … », « OK Géode … ») : retirée avant l'analyse. L'assistant s'appelle Géode
 * (Olivier, 19.10 : JARVIS n'était qu'un nom de chantier) ; « JARVIS » reste toléré pour les habitudes prises.
 */
export const stripAddress = (text: string): string =>
  text.replace(/^\s*(?:(?:ok|hey|dis|bonjour|salut)\s+)?(?:g[ée]ode|jarvis)\s*[,.!:]?\s*/i, '').trim() || text.trim();

/** Vrai quand la phrase demande la prochaine étape. */
export const asksNextStep = (text: string): boolean => {
  const normalized = normalizePhrase(text);
  return NEXT_STEP_PHRASES.some((phrase) => normalized.includes(phrase));
};

/** Nom de l'outil « crée un dossier … » (enregistré par l'Atelier, J2d). */
export const CREATE_DOSSIER_TOOL_NAME = 'create_dossier';

/**
 * « [JARVIS,] crée / créer / nouveau / create / new [un | une | a | the] dossier [nommé | appelé | called | named] {nom} » (fr + en, casse et accents libres)
 * : lue sur le texte ORIGINAL pour garder le nom tel que dit (guillemets et ponctuation finale retirés).
 */
const CREATE_DOSSIER_PATTERN =
  /^(?:(?:jarvis|g[ée]ode)\s*[,:!]?\s*)?(?:cr[ée]{1,2}[sz]?|cr[ée]er|nouveau|nouvelle|create|new|make)\s+(?:un\s+|une\s+|a\s+|le\s+|the\s+)?dossier\s+(?:(?:nomm[ée]|appel[ée]|called|named)\s+)?(.+?)\s*$/i;

/** Le nom du dossier demandé par la phrase, ou `null` quand la phrase ne demande pas un dossier. */
export const createDossierNameOf = (text: string): string | null => {
  const match = CREATE_DOSSIER_PATTERN.exec(text.trim());
  if (!match) {
    return null;
  }
  const name = match[1]
    .replace(/[.!?…]+$/, '')
    .trim()
    .replace(/^[«"'‘“]+|[»"'’”]+$/g, '')
    .trim();
  return name.length > 0 ? name : null;
};

/** Formulations de confirmation (normalisées) : la phrase COMMENCE par l'une d'elles. */
export const CONFIRM_YES_PHRASES: readonly string[] = ['oui', 'yes', 'ok', "d'accord", 'vas y', 'go', 'confirme', 'confirm'];
export const CONFIRM_NO_PHRASES: readonly string[] = ['non', 'no', 'annule', 'annuler', 'cancel', 'laisse tomber', 'nope'];

const startsWithWord = (normalized: string, phrase: string): boolean =>
  normalized === phrase || normalized.startsWith(`${phrase} `) || normalized.startsWith(`${phrase},`) || normalized.startsWith(`${phrase}.`) || normalized.startsWith(`${phrase}!`);

/** La décision entendue (« oui … » / « non … »), ou `null`. */
export const confirmDecisionOf = (text: string): VoiceConfirmDecision | null => {
  const normalized = normalizePhrase(text);
  if (CONFIRM_YES_PHRASES.some((phrase) => startsWithWord(normalized, phrase))) {
    return 'yes';
  }
  if (CONFIRM_NO_PHRASES.some((phrase) => startsWithWord(normalized, phrase))) {
    return 'no';
  }
  return null;
};

export const ideaToolOf = (text: string): { name: string; args: Record<string, unknown> } | null => {
  const create = /^(?:cr[\u00e9e]{1,2}[sz]?|cr[\u00e9e]er|create|make)\s+(?:un |une |a |an |the )?(?:module|id[\u00e9e]e|idea)\s+(.+)$/i.exec(text);
  if (create) return { name: 'create_idea', args: { ideaText: create[1] } };
  const normalized = normalizePhrase(text).replace(/[.!?]+$/, '');
  if (['cadre', 'lance le cadrage', 'start brief', 'start the brief', 'brief'].includes(normalized)) return { name: 'start_brief', args: {} };
  if (['produis le prototype', 'cree le prototype', 'produce the prototype', 'generate prototype', 'generate the prototype'].includes(normalized)) return { name: 'start_prototype', args: {} };
  if (['cree le dossier', 'create the dossier', 'create dossier'].includes(normalized)) return { name: 'transfer_idea', args: {} };
  if (['relis le cadrage', 'read the brief', 'read brief'].includes(normalized)) return { name: 'read_brief', args: {} };
  if (['annule', 'cancel', 'cancel phase'].includes(normalized)) return { name: 'cancel_phase', args: {} };
  return null;
};

export class MockVoiceSession implements VoiceSession {
  announce(text: string): void { if (this.running) useVoiceStore.getState().addTranscript('assistant', text); }
  /** Abandon de la phrase en cours seulement : « stop » laisse la session ouverte (revue CORE 11.10). */
  private phraseAbort: AbortController | null = null;
  private running = false;

  constructor(private readonly options: MockVoiceSessionOptions) {}

  async start(): Promise<void> {
    this.running = true;
    useVoiceStore.getState().setState('listening');
  }

  /** Une phrase validée dans la zone de saisie = une transcription finale. */
  async submitPhrase(phrase: string): Promise<void> {
    const said = phrase.trim();
    const text = stripAddress(said);
    if (!this.running || !said) {
      return;
    }
    const store = useVoiceStore.getState();
    store.addTranscript('user', said);
    store.setState('speaking');

    // J2d : une confirmation attend → « oui » / « non » la tranchent ; sinon « crée un dossier … » ; puis « que dois-je faire ? ».
    const decision = hasPendingVoiceConfirmation() ? confirmDecisionOf(text) : null;
    if (decision) {
      await this.runTool(CONFIRM_TOOL_NAME, { decision });
      return;
    }
    const dossierName = createDossierNameOf(text);
    const ideaTool = ideaToolOf(text);
    if (ideaTool && !(ideaTool.name === 'cancel_phase' && !hasVoiceTool('cancel_phase') && hasVoiceTool('ui_undo'))) {
      await this.runTool(ideaTool.name, ideaTool.args);
      return;
    }
    if (dossierName) {
      await this.runTool(CREATE_DOSSIER_TOOL_NAME, { name: dossierName });
      return;
    }
    if (asksNextStep(text)) {
      await this.askNextStep();
      return;
    }
    // J4c : « annule » défait le dernier remplissage de Géode, sauf quand l'Idée attend « annule la phase » (outil enregistré).
    if (isUndoPhrase(text) && hasVoiceTool('ui_undo') && !hasVoiceTool('cancel_phase')) {
      await this.runTool('ui_undo', {});
      return;
    }
    const uiCall = uiToolOf(text);
    if (uiCall && hasVoiceTool(uiCall.name)) {
      await this.runUiCall(uiCall);
      return;
    }

    const controller = new AbortController();
    this.phraseAbort = controller;
    const answer = await executeVoiceTool(
      { callId: `mock-${Date.now()}`, name: 'ask_copilot', argumentsJson: JSON.stringify({ question: text }) },
      {
        pageContext: this.options.pageContext,
        abortSignal: controller.signal,
        onCancel: () => {
          void this.cancel();
        },
      }
    );
    if (this.phraseAbort === controller) {
      this.phraseAbort = null;
    }
    if (!this.running || controller.signal.aborted) {
      return;
    }

    let answerText = '…';
    try {
      const parsed = JSON.parse(answer.outputJson) as { answer?: string; error?: string };
      answerText = parsed.answer ?? parsed.error ?? answerText;
    } catch {
      // sortie inattendue : le texte neutre reste
    }
    const current = useVoiceStore.getState();
    current.addTranscript('assistant', answerText);

    const lowered = text.toLowerCase();
    if (lowered.includes('montre') || lowered.includes('affiche')) {
      await executeVoiceTool(
        {
          callId: `mock-card-${Date.now()}`,
          name: 'show_card',
          argumentsJson: JSON.stringify({ title: text, bodyMd: answerText }),
        },
        { pageContext: this.options.pageContext }
      );
    }
    useVoiceStore.getState().setState('listening');
  }

  /** « Que dois-je faire ? » : l'outil `next_step` par le chemin de la session réelle ; hors fiche, la réponse « Ouvre d'abord un dossier ». */
  private async askNextStep(): Promise<void> {
    const result = await executeVoiceTool(
      { callId: `mock-next-${Date.now()}`, name: NEXT_STEP_TOOL_NAME, argumentsJson: '{}' },
      { pageContext: this.options.pageContext }
    );
    if (!this.running) {
      return;
    }
    let answerText: string | null = null;
    try {
      const parsed = JSON.parse(result.outputJson) as { answer?: string; error?: string };
      answerText = typeof parsed.answer === 'string' ? parsed.answer : null;
    } catch {
      // sortie inattendue : la réponse « hors fiche » reste
    }
    const noDossierId = 'voice.tool.next_step.noDossier';
    const current = useVoiceStore.getState();
    current.addTranscript('assistant', answerText ?? this.options.translate?.(noDossierId) ?? noDossierId);
    current.setState('listening');
  }

  /**
   * J2d : un outil du registre par le chemin de la session réelle ; le texte rendu (`answer`) est la transcription de l'assistant. Une erreur lisible
   * (`nothing_pending` : rien n'attend ; `unknown_tool` : hors Atelier) est traduite quand `translate` le permet, sinon le code est affiché.
   */
  private async runTool(name: string, args: Record<string, unknown>): Promise<void> {
    const result = await executeVoiceTool(
      { callId: `mock-${name}-${Date.now()}`, name, argumentsJson: JSON.stringify(args) },
      { pageContext: this.options.pageContext }
    );
    if (!this.running) {
      return;
    }
    let answerText = '…';
    try {
      const parsed = JSON.parse(result.outputJson) as { answer?: string; error?: string };
      if (typeof parsed.answer === 'string') {
        answerText = parsed.answer;
      } else if (parsed.error === 'nothing_pending') {
        answerText = this.options.translate?.('voice.confirm.nothingPending') ?? parsed.error;
      } else if (typeof parsed.error === 'string') {
        answerText = parsed.error;
      }
    } catch {
      // sortie inattendue : le texte neutre reste
    }
    const current = useVoiceStore.getState();
    current.addTranscript('assistant', answerText);
    current.setState('listening');
  }

  /**
   * J4c : un geste d'écran ; tant que l'outil ne trouve rien (`not_found`), les appels de repli sont essayés dans l'ordre (« ouvre X » : le menu, puis
   * l'élément de l'écran, puis le complément du nom). Seule la dernière réponse devient la transcription de l'assistant.
   */
  private async runUiCall(call: { name: string; args: Record<string, unknown>; fallbacks?: { name: string; args: Record<string, unknown> }[] }): Promise<void> {
    const attempts = [{ name: call.name, args: call.args }, ...(call.fallbacks ?? [])].filter((attempt) => hasVoiceTool(attempt.name));
    for (const attempt of attempts) {
      const result = await executeVoiceTool(
        { callId: `mock-${attempt.name}-${Date.now()}`, name: attempt.name, argumentsJson: JSON.stringify(attempt.args) },
        { pageContext: this.options.pageContext }
      );
      if (!this.running) {
        return;
      }
      let parsed: { answer?: string; error?: string; highlighted?: boolean } = {};
      try {
        parsed = JSON.parse(result.outputJson) as { answer?: string; error?: string; highlighted?: boolean };
      } catch {
        // sortie inattendue : la réponse neutre reste
      }
      // Rien trouvé (ou un nom interdit qu'aucun bouton ne porte, donc rien mis en évidence) : l'essai suivant.
      const nothingFound = parsed.error === 'not_found' || (parsed.error === 'forbidden' && parsed.highlighted === false);
      if (nothingFound && attempt !== attempts[attempts.length - 1]) {
        continue;
      }
      const current = useVoiceStore.getState();
      current.addTranscript('assistant', typeof parsed.answer === 'string' ? parsed.answer : (parsed.error ?? '…'));
      current.setState('listening');
      return;
    }
  }

  /** Abandonne l'appel copilote en cours. */
  async cancel(): Promise<void> {
    this.phraseAbort?.abort();
    this.phraseAbort = null;
    useVoiceStore.getState().setState('listening');
  }

  async stop(): Promise<void> {
    this.running = false;
    this.phraseAbort?.abort();
    this.phraseAbort = null;
    useVoiceStore.getState().setState('idle');
  }
}
